import { BrowserMultiFormatOneDReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat as ZxingBarcodeFormat } from "@zxing/library";

const removeReadonly = <T>(arr: T[] | readonly T[]): T[] => arr as T[];

async function polyfill() {
  if (!("BarcodeDetector" in window)) {
    try {
      await import("barcode-detector");
    } catch {
      throw new Error("BarcodeDetector API is not supported by your browser.");
    }
  }
}

export async function getBarcodeReader(formats?: BarcodeFormat[]): Promise<BarcodeReader> {
  return polyfill().then(() =>
    window.BarcodeDetector.getSupportedFormats().then(
      (supportedFormats) => new BarcodeReader(formats || supportedFormats),
    ),
  );
}

/**
 * BarcodeReader class to detect barcodes from images or videos.
 *
 * @see https://developer.mozilla.org/docs/Web/API/BarcodeDetector
 * @see https://github.com/georapbox/barcode-scanner/blob/main/src/js/helpers/BarcodeReader.js
 */
export default class BarcodeReader {
  private barcodeDetector: BarcodeDetector | undefined = undefined;

  async detect(source: ImageData | HTMLVideoElement): Promise<string> {
    if (!this.barcodeDetector) {
      throw new Error("BarcodeReader is not initialized.");
    }
    if (source instanceof HTMLVideoElement && source.readyState != source.HAVE_ENOUGH_DATA) {
      throw new Error("Not ready.");
    }

    const results = await this.barcodeDetector.detect(source);

    if (Array.isArray(results) && results.length > 0) {
      const result = results[0];
      if (result.rawValue.length <= 0) {
        throw new Error("No data.");
      }
      return result.rawValue;
    } else {
      throw new Error("Could not detect barcode from provided source.");
    }
  }

  zxingDetect(data: HTMLVideoElement, formats: Array<ZxingBarcodeFormat> = []): string {
    let hints = new Map<DecodeHintType, unknown>();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
    hints.set(DecodeHintType.TRY_HARDER, true);

    const reader = new BrowserMultiFormatOneDReader(hints);
    return reader.decode(data).getText();
  }

  constructor(formats: readonly BarcodeFormat[]) {
    const _formats = removeReadonly(formats);
    this.barcodeDetector = new window.BarcodeDetector({ formats: _formats });
  }
}
